import moment from 'moment-timezone';
import authFetch from "../utils/authFetch";

const BASE_CHART_URL = "https://aaps.space/draw-kundli/testtubechart.php";

/**
 * Generate chart URLs from chart data
 * @param {Object} data - Chart data containing birthChart, yearChart, dayChart
 * @param {Function} setH1 - Function to set h1 value (optional)
 * @returns {Object} Object containing chart URLs
 */


export const generateChartUrls = (data, setH1 = null) => {
  try {
    const urls = {};

    // Extract h1 from birth chart if available
    let currentH1 = 1;
    if (
      data.birthChart &&
      data.birthChart.data &&
      data.birthChart.data.birthChart &&
      data.birthChart.data.birthChart[0] &&
      data.birthChart.data.birthChart[0].signNumber
    ) {
      currentH1 = data.birthChart.data.birthChart[0].signNumber;
      if (setH1) setH1(currentH1);
    }

    // Birth Chart URL
    if (
      data.birthChart &&
      data.birthChart.data &&
      data.birthChart.data.birthChart
    ) {
      const birthChartData = data.birthChart.data.birthChart;
      const birthChartQueries = birthChartData
        .flatMap((entry) => {
          if (
            entry.planet &&
            Array.isArray(entry.planet) &&
            entry.planet.length > 0
          ) {
            return entry.planet.map(
              (planet) => `${planet.toLowerCase()}=${entry.house}`
            );
          }
          return [];
        })
        .filter((query) => query);

      if (birthChartQueries.length > 0) {
        urls.birthChartUrl = `${BASE_CHART_URL}?h1=${currentH1}&brand=0&${birthChartQueries.join(
          "&"
        )}`;
      }
    }

    // Year Chart URL
    if (
      data.yearChart &&
      data.yearChart.data &&
      data.yearChart.data.yearChart
    ) {
      const yearChartData = data.yearChart.data.yearChart;
      const yearlyQueries = yearChartData
        .flatMap((house) => {
          if (
            house.planet &&
            Array.isArray(house.planet) &&
            house.planet.length > 0
          ) {
            return house.planet.map(
              (planet) => `${planet.toLowerCase()}=${house.house}`
            );
          }
          return [];
        })
        .filter((query) => query);

      if (yearlyQueries.length > 0) {
        urls.yearlyChartUrl = `${BASE_CHART_URL}?h1=${currentH1}&brand=0&${yearlyQueries.join(
          "&"
        )}`;
      }
    }

    // Month Chart URL - from day chart response
    if (
      data.dayChart &&
      data.dayChart.data &&
      data.dayChart.data.monthChart
    ) {
      const monthChartData = data.dayChart.data.monthChart;
      const monthlyQueries = monthChartData
        .flatMap((house) => {
          if (
            house.planet &&
            Array.isArray(house.planet) &&
            house.planet.length > 0
          ) {
            return house.planet.map(
              (planet) => `${planet.toLowerCase()}=${house.house}`
            );
          }
          return [];
        })
        .filter((query) => query);

      if (monthlyQueries.length > 0) {
        urls.monthlyChartUrl = `${BASE_CHART_URL}?h1=${currentH1}&brand=0&${monthlyQueries.join(
          "&"
        )}`;
      }
    }

    // Day Chart URL
    if (data.dayChart && data.dayChart.data && data.dayChart.data.dayChart) {
      const dayChartData = data.dayChart.data.dayChart;
      const dailyQueries = dayChartData
        .flatMap((house) => {
          if (
            house.planet &&
            Array.isArray(house.planet) &&
            house.planet.length > 0
          ) {
            return house.planet.map(
              (planet) => `${planet.toLowerCase()}=${house.house}`
            );
          }
          return [];
        })
        .filter((query) => query);

      if (dailyQueries.length > 0) {
        urls.dailyChartUrl = `${BASE_CHART_URL}?h1=${currentH1}&brand=0&${dailyQueries.join(
          "&"
        )}`;
      }
    }

    return urls;
  } catch (err) {
    console.error("Error generating chart URLs:", err);
    return {};
  }
};

/**
 * Fetch all charts for a user
 * @param {string} userId - User ID
 * @param {Object} user - User object with birth details (optional)
 * @param {string} userCurrentTimezone - User's current timezone (optional)
 * @returns {Promise<Object>} Object containing all chart data
 */


// Add the getPredictionYear function definition here
export function getPredictionYear(
  dob,
  currentDay,
  currentMonth,
  userCurrentTimezone
){
  // Current time in user's timezone
  const now = moment.tz(userCurrentTimezone);
  const currentYear = now.year();
  const dobMoment = moment(dob).tz(userCurrentTimezone);
  const dobDay = dobMoment.date();
  const dobMonth = dobMoment.month() + 1; // moment months are 0-indexed
  
  if (
    currentMonth < dobMonth ||
    (currentMonth === dobMonth && currentDay < dobDay)
  ) {
    // Birthday not yet reached this year
    return currentYear;
  } else {
    // Birthday passed this year
    return currentYear - 1;
  }
}


export const fetchAllCharts = async (userId, user = null, userCurrentTimezone = null) => {
  const currentMoment = moment();
  const currentMonth = currentMoment.month() + 1;
  const currentDay = currentMoment.date();

  // Determine timezone to use
  const timezoneToUse = userCurrentTimezone || 
    (user && user.userCurrentTimezone) || 
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [birthResponse, yearResponse, dayResponse] = await Promise.all([
    authFetch.get(
      `${process.env.REACT_APP_BACKEND_URL}/worker/admin/chart/birth/${userId}`
    ),
    authFetch.post(
      `${process.env.REACT_APP_BACKEND_URL}/worker/admin/chart/year`,
      {
        userId,
        userCurrentTimezone: timezoneToUse,
      }
    ),
    authFetch.post(
      `${process.env.REACT_APP_BACKEND_URL}/worker/admin/chart/day`,
      {
        userId,
        month: currentMonth,
        day: currentDay,
        userCurrentTimezone: timezoneToUse,
      }
    ),
  ]);


  return {
    birthChart: birthResponse.data,
    yearChart: yearResponse.data,
    dayChart: dayResponse.data,
  };
};

/**
 * Fetch transit chart for a specific date
 * @param {string} userId - User ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {number} day - Day
 * @param {string} userCurrentTimezone - User's timezone
 * @returns {Promise<Object>} Transit chart data
 */
export const fetchTransitChart = async (userId, year, month, day, userCurrentTimezone) => {
  const response = await authFetch.post(
    `${process.env.REACT_APP_BACKEND_URL}/worker/admin/chart/transit`,
    {
      userId,
      year: Number(year),
      month: Number(month),
      day: Number(day),
      userCurrentTimezone,
    }
  );
  return response.data;
};

/**
 * Fetch year, month, and day charts for lalkitab
 * @param {string} userId - User ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {number} day - Day
 * @param {string} userCurrentTimezone - User's timezone
 * @returns {Promise<Object>} Year, month, and day chart data
 */
export const fetchLalkitabCharts = async (userId, year, month, day, userCurrentTimezone) => {
  const [yearResponse, dayResponse] = await Promise.all([
    authFetch.post(
      `${process.env.REACT_APP_BACKEND_URL}/worker/admin/chart/year`,
      {
        userId,
        year: Number(year),
        userCurrentTimezone,
      }
    ),
    authFetch.post(
      `${process.env.REACT_APP_BACKEND_URL}/worker/admin/chart/day`,
      {
        userId,
        year: Number(year),
        month: Number(month),
        day: Number(day),
        userCurrentTimezone,
      }
    ),
  ]);

  return {
    yearChart: yearResponse.data,
    dayChart: dayResponse.data,
  };
};

/**
 * Generate transit chart URL
 * @param {Object} transitChartData - Transit chart data
 * @returns {string} Transit chart URL
 */
export const generateTransitChartUrl = (transitChartData) => {
  if (!transitChartData || !transitChartData.data || !transitChartData.data.transitChart) {
    return '';
  }

  const transitQueries = transitChartData.data.transitChart
    .flatMap((house) => {
      if (house.planet && Array.isArray(house.planet) && house.planet.length > 0) {
        return house.planet.map(
          (planet) => `${planet.toLowerCase()}=${house.house}`
        );
      }
      return [];
    })
    .filter((query) => query);

  if (transitQueries.length > 0) {
    return `${BASE_CHART_URL}?h1=1&brand=0&${transitQueries.join("&")}`;
  }

  return '';
};